// Unpublished Work © 2023 Deere & Company.

import { LicenseInfo } from "@mui/x-license-pro";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { useContext, useEffect } from "react";
import { ApiContextProvider } from "./context/ApiContext/ApiContext";
import { ConfigContext } from "./context/ConfigContext";
import {
  DelegateProvider,
  useDelegateContext,
} from "./context/DelegateContext/DelegateContext";
import { RBACAccessProvider } from "./context/RBACAccessContext/RBACAccessContext";
import { initializeDatadog } from "./datadog";
import Router from "./router/Router";

const IdentityScopedApp = ({
  enableApiContext,
}: {
  enableApiContext: boolean;
}) => {
  const { delegatedUserId } = useDelegateContext();

  const identityProviders = (
    <RBACAccessProvider>
      <Router />
    </RBACAccessProvider>
  );

  return enableApiContext ? (
    <ApiContextProvider key={delegatedUserId ?? "authenticated-user"}>
      {identityProviders}
    </ApiContextProvider>
  ) : (
    <RBACAccessProvider key={delegatedUserId ?? "authenticated-user"}>
      <Router />
    </RBACAccessProvider>
  );
};

const App = () => {
  const {
    OKTA_AUTH_SERVER_URL,
    OKTA_CLIENT_ID,
    EXTRA_OKTA_AUTH_SCOPES,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_OKTA_SCOPES,
    NODE_ENV,
    DATADOG_APPID,
    DATADOG_CLIENT_TOKEN,
    DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    ENABLE_DATADOG_RUM,
    ENABLE_DATADOG_UNHANDLED_REJECTION_GUARD,
    ENABLE_DELEGATE_LOGIN,
  } = useContext(ConfigContext);

  useEffect(() => {
    if (ENABLE_DATADOG_RUM) {
      initializeDatadog(
        DATADOG_APPID,
        DATADOG_CLIENT_TOKEN,
        DATADOG_SESSION_REPLAY_SAMPLE_RATE,
        NODE_ENV,
        ENABLE_DATADOG_UNHANDLED_REJECTION_GUARD
      );
    }
  }, [
    ENABLE_DATADOG_RUM,
    DATADOG_APPID,
    DATADOG_CLIENT_TOKEN,
    DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    NODE_ENV,
    ENABLE_DATADOG_UNHANDLED_REJECTION_GUARD,
  ]);

  const extraScopes = ENABLE_OKTA_SCOPES
    ? (EXTRA_OKTA_AUTH_SCOPES || "").trim().split(" ")
    : [];

  const oktaAuth = new OktaAuth({
    issuer: OKTA_AUTH_SERVER_URL,
    redirectUri: `${window.location.origin}/implicit/callback`,
    clientId: OKTA_CLIENT_ID,
    pkce: true,
    scopes: ["openid", "profile", ...extraScopes],
  });

  oktaAuth.tokenManager.on("error", (err: any) => {
    console.warn("[TokenManager] Renewal failed:", err);
  });

  const restoreOriginalUri = (_oktaAuth: any, originalUri: any) =>
    window.location.replace(
      toRelativeUrl(originalUri || "/", window.location.origin)
    );

  if (process.env.REACT_APP_MUI_PRO_LICENSE_KEY)
    LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_PRO_LICENSE_KEY);

  const app = ENABLE_RBAC_AUTH_SUPPORT ? (
    <ApiContextProvider>
      <RBACAccessProvider>
        <Router />
      </RBACAccessProvider>
    </ApiContextProvider>
  ) : (
    <RBACAccessProvider>
      <Router />
    </RBACAccessProvider>
  );

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {ENABLE_DELEGATE_LOGIN ? (
        <DelegateProvider>
          <IdentityScopedApp enableApiContext={ENABLE_RBAC_AUTH_SUPPORT} />
        </DelegateProvider>
      ) : (
        app
      )}
    </Security>
  );
};

export default App;
